import { useState } from 'react';
import { Box, SimpleGridProps } from '@chakra-ui/react';
import { Formik } from 'formik';
import { validateRequired, validateConfirm, validatePassword } from 'utils/validation';
import {
  errorMessageConfirmPassword,
  errorMessagePasswordShort,
  errorMessagePasswordStrength,
} from 'constants/validation';
import { FormLabel, FormControl, Input, Button, SimpleGrid } from 'components/UI';
import { validateForm } from 'utils/helpers';

export type RecoverPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

export type RecoverPasswordFormProps = {
  onSubmit: (values: RecoverPasswordFormValues) => void;
  columns?: number[] | number;
  spacing?: number;
  gridProps?: SimpleGridProps;
};

const RecoverPasswordForm = ({ onSubmit, columns = [2], spacing = 8, gridProps = {} }: RecoverPasswordFormProps) => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <Formik
      initialValues={{ password: '', confirmPassword: '' }}
      validate={async (values: RecoverPasswordFormValues) => {
        const errors = validateRequired<RecoverPasswordFormValues>(['password', 'confirmPassword'], values);

        if (values.password) {
          const score = validatePassword(values.password);
          if (!score) {
            errors.password = errorMessagePasswordShort;
          } else if (score && score < 3) {
            errors.password = errorMessagePasswordStrength;
          }
        }

        if (values.password && values.confirmPassword && !validateConfirm(values.password, values.confirmPassword)) {
          errors.confirmPassword = errorMessageConfirmPassword;
        }

        return errors;
      }}
      onSubmit={onSubmit}
    >
      {(formConfig) => {
        const { values, handleChange, handleBlur, handleSubmit, isSubmitting } = formConfig;

        const formErrors = validateForm<RecoverPasswordFormValues>({
          ...formConfig,
          submitted,
        });

        return (
          <form onSubmit={handleSubmit}>
            <SimpleGrid columns={columns} spacing={spacing} mb={4} {...gridProps}>
              <Box>
                <FormLabel htmlFor="password">NEW PASSWORD *</FormLabel>

                <FormControl isInvalid={!!formErrors.password} mb={2}>
                  <Input
                    type="password"
                    name="password"
                    maxLength={20}
                    value={values.password}
                    error={formErrors.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    placeholder="Enter your new password"
                    isTogglePassword
                    isPasswordStrength
                  />
                </FormControl>
              </Box>

              <Box>
                <FormLabel htmlFor="confirmPassword">CONFIRM PASSWORD *</FormLabel>

                <FormControl isInvalid={!!formErrors.confirmPassword}>
                  <Input
                    type="password"
                    name="confirmPassword"
                    maxLength={20}
                    value={values.confirmPassword}
                    error={formErrors.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    placeholder="Confirm your password"
                    isTogglePassword={false}
                  />
                </FormControl>
              </Box>
            </SimpleGrid>
            <Button
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => setSubmitted(true)}
              type="submit"
              size="lg"
              maxW="full"
            >
              SUBMIT
            </Button>
          </form>
        );
      }}
    </Formik>
  );
};

export default RecoverPasswordForm;
